import React from 'react';
import { Box, Container, Typography, styled, Collapse } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AnimatedSection from '../../components/AnimatedSection';
import '../../glow-effect.css';

const BASE_PATH = '/peoplefortech/v2';

const ServicesCirclesV2 = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showSecondary, setShowSecondary] = React.useState(false);

  const MainCard = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: '100%',
    maxWidth: '420px',
    minHeight: '420px',
    margin: '0 auto 40px',
    padding: '40px 50px',
    backgroundColor: 'rgba(255, 255, 255, 0.65)',
    backdropFilter: 'blur(10px)',
    borderRadius: '16px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease, background-color 0.3s ease',
    color: '#000',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)',
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
    },
    [theme.breakpoints.down('sm')]: {
      maxWidth: '320px',
      minHeight: '380px',
      padding: '30px 25px',
    },
  }));

  const BlackCircle = styled(Box)(({ theme }) => ({
    width: '180px',
    height: '180px',
    borderRadius: '50%',
    backgroundColor: '#000000',
    border: '2px solid rgba(0, 0, 0, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(3),
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
    [theme.breakpoints.down('sm')]: {
      width: '150px',
      height: '150px',
    },
  }));

  const SubitemBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
  }));

  const SubitemChip = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1, 2),
    backgroundColor: '#000000',
    color: '#ffffff',
    borderRadius: '10px',
    fontSize: '0.85rem',
    fontWeight: 600,
    fontFamily: '"Montserrat", sans-serif',
  }));

  const SecondarySection = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
    width: '100%',
    maxWidth: '800px',
    margin: '0 auto',
  }));

  const SecondaryCard = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 3),
    marginBottom: theme.spacing(1.5),
    backgroundColor: 'rgba(255,255,255,0.7)',
    backdropFilter: 'blur(10px)',
    borderRadius: '12px',
    cursor: 'pointer',
    border: '1px solid rgba(0,0,0,0.08)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.9)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.spacing(1),
    },
  }));

  const secondaryServices = [
    { key: 'development', titleKey: 'v2.services.development.title', descKey: 'v2.services.development.description', route: '/coming-soon', state: { serviceKey: 'development' } },
    { key: 'seo', titleKey: 'v2.services.seo.title', descKey: 'v2.services.seo.description', route: '/seo-example', state: null },
  ];

  const handleMainClick = () => {
    navigate(`${BASE_PATH}/contact`, { state: { serviceKey: 'consultory' } });
  };

  const handleSecondaryClick = (s) => {
    if (s.route === '/seo-example') {
      navigate(s.route);
    } else {
      navigate(s.route, { state: s.state });
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes cardAnimate {
            0%, 100% { transform: translateY(10px); }
            50% { transform: translateY(-10px); }
          }
        `}
      </style>
      <Container sx={{ py: 8, px: 2, backgroundColor: 'transparent', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <AnimatedSection direction="bottom" delay={0.2}>
          <MainCard onClick={handleMainClick}>
            <BlackCircle>
              <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '1.1rem', textAlign: 'center', textTransform: 'uppercase', fontFamily: '"Montserrat", sans-serif', px: 2 }}>
                {t('v2.services.consultory.title')}
              </Typography>
            </BlackCircle>
            <Typography sx={{ color: '#333', fontSize: '1rem', textAlign: 'center', lineHeight: 1.6, mb: 2 }}>
              {t('v2.services.consultory.description')}
            </Typography>
            <SubitemBox>
              <SubitemChip component="span">{t('v2.services.consultory.subitems.audit')}</SubitemChip>
              <SubitemChip component="span">{t('v2.services.consultory.subitems.roadmap')}</SubitemChip>
              <SubitemChip component="span">{t('v2.services.consultory.subitems.cases')}</SubitemChip>
            </SubitemBox>
          </MainCard>
        </AnimatedSection>

        <SecondarySection>
          <Box
            onClick={() => setShowSecondary(!showSecondary)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              cursor: 'pointer',
              color: '#666',
              fontWeight: 500,
              mb: 2,
              '&:hover': { color: '#000' },
            }}
          >
            <Typography variant="body2">{t('v2.services.otherServices')}</Typography>
            <ExpandMoreIcon sx={{ transform: showSecondary ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }} />
          </Box>
          <Collapse in={showSecondary}>
            {secondaryServices.map((s) => (
              <SecondaryCard key={s.key} onClick={() => handleSecondaryClick(s)}>
                <Typography fontWeight={600} color="#000">{t(s.titleKey)}</Typography>
                <Typography variant="body2" color="#666" sx={{ maxWidth: 400 }}>{t(s.descKey)}</Typography>
              </SecondaryCard>
            ))}
          </Collapse>
        </SecondarySection>
      </Container>
    </>
  );
};

export default ServicesCirclesV2;
